import React, { useState } from 'react';
import { Plus, Mail, Phone, Edit3, UserX, UserCheck, Award, Search, Trophy, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useListUsersQuery, useDeactivateUserMutation, useUpdateUserMutation } from '../../api/usersApi';
import { useStaffPerformanceQuery } from '../../api/reportsApi';
import { colorForDoctor, doctorDisplayName } from '../appointments/doctorColors';
import StaffFormModal from './StaffFormModal';
import StaffPerformanceDrawer from './StaffPerformanceDrawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import './staff.css';

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);

const TABS = [
  { key: 'doctors',    label: 'Doctors',    role: 'Doctor' },
  { key: 'therapists', label: 'Therapists', role: 'Therapist' },
  { key: 'all',        label: 'All Staff',  role: null },
];

export default function StaffPage() {
  const [tab, setTab] = useState('doctors');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [perfUserId, setPerfUserId] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  const activeTab = TABS.find((t) => t.key === tab);
  const { data: performance = [] } = useStaffPerformanceQuery({});
  const topPerformers = performance.slice(0, 3);

  const { data, isFetching, isError, refetch } = useListUsersQuery({
    role: activeTab?.role ?? undefined,
    search,
    pageSize: 200,
  });

  const [deactivate, { isLoading: deactivating }] = useDeactivateUserMutation();
  const [updateUser] = useUpdateUserMutation();

  const reactivate = async (u) => {
    try {
      await updateUser({
        id: u.id,
        fullName: u.fullName,
        phoneNumber: u.phoneNumber ?? null,
        colorTag: u.colorTag ?? null,
        specialization: u.specialization ?? null,
        licenseNumber: u.licenseNumber ?? null,
        bio: u.bio ?? null,
        isActive: true,
      }).unwrap();
      toast.success(`${u.fullName} reactivated`);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not reactivate staff member.');
    }
  };

  // Counts for tab badges (rough — one query per tab would be heavy; we only show count for active tab)
  const tabCount = data?.totalCount ?? (data?.items?.length ?? 0);

  const handleDeactivate = async () => {
    if (!confirmDeactivate) return;
    try {
      await deactivate(confirmDeactivate.id).unwrap();
      toast.success(`Deactivated ${confirmDeactivate.fullName}`);
      setConfirmDeactivate(null);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not deactivate');
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Staff</span></div>
        <div className="page-header-row">
          <div>
            <h1>Staff</h1>
            <div className="page-header-sub">Manage doctors, therapists, and other clinic users.</div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={16} /> Add Staff
          </button>
        </div>
      </div>

      <div className="staff-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`staff-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {tab === t.key && <span className="count">{tabCount}</span>}
          </button>
        ))}
      </div>

      <div className="staff-search-bar">
        <div className="search-field" style={{ flex: 1 }}>
          <Search size={14} className="si" />
          <input
            className="form-control"
            placeholder={`Search ${activeTab?.label.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {topPerformers.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header"><h3><Trophy size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Top performers (last 12 months)</h3></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {topPerformers.map((s, i) => (
              <button key={s.userId} className="card" style={{ padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }} onClick={() => setPerfUserId(s.userId)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{doctorDisplayName(s.fullName)}</strong>
                  <span className="badge b-amber">#{i + 1}</span>
                </div>
                <div className="td-muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  {s.sessionsConducted} sessions · {s.consultations} consults · {s.patientsHandled} patients
                </div>
                <div style={{ fontWeight: 800, marginTop: 4, color: 'var(--green-700)' }}>{fmtINR(s.revenueAttributed)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isError && (
        <div className="auth-error" style={{ marginBottom: 14 }}>
          Could not load staff. <button className="btn btn-sm btn-outline" onClick={refetch}>Retry</button>
        </div>
      )}

      {isFetching && (!data?.items?.length) && (
        <div className="skeleton" style={{ height: 120, marginBottom: 14 }} />
      )}

      <div className="staff-grid">
        {(data?.items ?? []).map((u) => {
          const color = colorForDoctor({ id: u.id, colorTag: u.colorTag });
          const initials = (u.fullName ?? 'NN').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
          const isDoctor = (u.roles ?? []).includes('Doctor');
          return (
            <div
              key={u.id}
              className={`staff-card ${u.isActive === false ? 'inactive' : ''}`}
              onClick={() => setPerfUserId(u.id)}
              title="View performance"
            >
              <div className="staff-card-stripe" style={{ background: color.dot }} />
              <div className="staff-card-top">
                <div className="staff-card-avatar" style={{
                  background: color.bg,
                  color: color.text,
                  borderColor: color.border,
                }}>{initials}</div>
                <div className="staff-card-meta">
                  <div className="staff-card-name">
                    {isDoctor ? doctorDisplayName(u.fullName) : u.fullName}
                  </div>
                  <div className="staff-card-role">
                    {u.roles?.join(' · ') || '—'}
                  </div>
                  {isDoctor && u.specialization && (
                    <div className="staff-card-spec"><Award size={11} /> {u.specialization}</div>
                  )}
                </div>
              </div>

              <div className="staff-card-contact">
                <div className="staff-card-contact-row">
                  <Mail size={12} /> {u.email}
                </div>
                {u.phoneNumber && (
                  <div className="staff-card-contact-row">
                    <Phone size={12} /> {u.phoneNumber}
                  </div>
                )}
              </div>

              <div className="staff-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="btn btn-sm btn-outline" onClick={() => setPerfUserId(u.id)}>
                  <BarChart3 size={12} /> Performance
                </button>
                <button className="btn btn-sm btn-outline" onClick={() => setEditing(u)}>
                  <Edit3 size={12} /> Edit
                </button>
                {u.isActive !== false && (
                  <button
                    className="btn btn-sm btn-outline"
                    style={{ color: 'var(--red-500)', borderColor: 'var(--red-100)' }}
                    onClick={() => setConfirmDeactivate(u)}
                    title="Deactivate"
                  >
                    <UserX size={12} /> Deactivate
                  </button>
                )}
                {u.isActive === false && (
                  <>
                    <span className="badge b-gray">Inactive</span>
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ color: 'var(--green-700)', borderColor: 'var(--green-100, #bbf7d0)' }}
                      onClick={() => reactivate(u)}
                      title="Reactivate — restores them to calendars & scheduling"
                    >
                      <UserCheck size={12} /> Reactivate
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {!isFetching && data?.items?.length === 0 && (
          <div className="card card-body" style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <h3>No {activeTab?.label.toLowerCase()} yet</h3>
              <p>
                Click "Add Staff" to create your first {activeTab?.role
                  ? activeTab.role.toLowerCase()
                  : 'staff member'}.
              </p>
            </div>
          </div>
        )}
      </div>

      {showAdd && (
        <StaffFormModal
          defaultRole={activeTab?.role ?? 'Doctor'}
          onClose={() => setShowAdd(false)}
        />
      )}

      {editing && (
        <StaffFormModal
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {perfUserId && (
        <StaffPerformanceDrawer userId={perfUserId} onClose={() => setPerfUserId(null)} />
      )}

      <ConfirmDialog
        open={Boolean(confirmDeactivate)}
        title="Deactivate staff member?"
        message={`${confirmDeactivate?.fullName ?? ''} will no longer be able to log in. Existing appointments are preserved. You can reactivate from the edit screen any time.`}
        confirmLabel={deactivating ? 'Deactivating…' : 'Deactivate'}
        variant="danger"
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </>
  );
}
